// Minimal toast/notification system. One context, one provider at the root,
// one `useToast()` hook anywhere in the tree. Toasts auto-dismiss after 4s
// and stack bottom-right. No framer-motion — plain Tailwind fade.
import { createContext, useCallback, useContext, useEffect, useRef, useState, type ReactNode } from "react";

type Kind = "success" | "error" | "info";
type Toast = { id: string; kind: Kind; message: string };
type Ctx = {
  push: (message: string, kind?: Kind) => void;
  success: (message: string) => void;
  error: (message: string) => void;
};

const ToastCtx = createContext<Ctx | null>(null);

export function ToastProvider({ children }: { children: ReactNode }) {
  const [items, setItems] = useState<Toast[]>([]);
  const idRef = useRef(0);

  const push = useCallback((message: string, kind: Kind = "info") => {
    const id = String(++idRef.current);
    setItems((prev) => [...prev, { id, kind, message }]);
    setTimeout(() => setItems((prev) => prev.filter((t) => t.id !== id)), 4000);
  }, []);
  const success = useCallback((m: string) => push(m, "success"), [push]);
  const error = useCallback((m: string) => push(m, "error"), [push]);

  return (
    <ToastCtx.Provider value={{ push, success, error }}>
      {children}
      <div className="fixed bottom-4 right-4 z-50 flex flex-col gap-2 max-w-[320px] safe-bottom pointer-events-none">
        {items.map((t) => (
          <div
            key={t.id}
            className={
              "rounded-xl border px-3 py-2 text-xs shadow-lg backdrop-blur pointer-events-auto " +
              (t.kind === "success"
                ? "border-px-green/50 bg-px-green/15 text-px-green"
                : t.kind === "error"
                ? "border-red-400/50 bg-red-400/15 text-red-200"
                : "border-px-line bg-px-surface-hi text-px-text")
            }
          >
            {t.message}
          </div>
        ))}
      </div>
    </ToastCtx.Provider>
  );
}

export function useToast(): Ctx {
  const ctx = useContext(ToastCtx);
  if (!ctx) throw new Error("useToast must be called inside <ToastProvider>");
  return ctx;
}

/**
 * Optional helper for mutation onSuccess/onError. Usage:
 *   const toast = useToast();
 *   const m = useMutation({ onSuccess: () => toast.success("Saved") });
 */
export function useMutationToasts() {
  const toast = useToast();
  useEffect(() => {}, []); // placeholder for future global listeners
  return toast;
}
